/**
 * The namespace/relation rules shared by the Permissions list and the tuple
 * form. Both screens ask the same three questions — which object types exist,
 * which relations may I offer for one, and what happens to a chosen relation
 * when the type changes — and both used to answer them with their own copy.
 */
import { useEffect, useState } from 'react';
import { hub } from '@/lib/hub-client';

/** `{ track: ['admin', 'member', 'viewer'], … }` — DIRECT relations only. */
export type NamespaceRelations = Record<string, string[]>;

/**
 * The relations offerable for one object type; the union of every namespace
 * when none is chosen.
 *
 * The source is `/relations/namespaces`, which returns DIRECT relations only.
 * That is the right set for BOTH screens: a computed relation (`track#read`)
 * is never stored as a tuple, so offering one would filter to an empty page on
 * the list and write dead authorization state from the form (P-41.3).
 *
 * Relations are namespace-scoped — `editor` exists on `prompt` and
 * `db_connector` but never on `track` — so an unnarrowed list would let an
 * operator build `track` + `editor`, a pair that can never match or grant.
 */
export function relationOptionsFor(namespaceRelations: NamespaceRelations, objectType: string): string[] {
  const relations = objectType
    ? (namespaceRelations[objectType] ?? [])
    : Object.values(namespaceRelations).flat();
  return [...new Set(relations)].sort();
}

/** Keeps a chosen relation only while it survives the new object type. */
export function relationAfterTypeChange(options: string[], relation: string): string {
  return options.includes(relation) ? relation : '';
}

/**
 * Loads the namespace map once. Stays EMPTY when the fetch fails (non-admin,
 * hub unreachable) — every caller reads that as "no closed set to offer" and
 * falls back to free text rather than showing an empty dropdown.
 */
export function useNamespaceRelations(): NamespaceRelations {
  const [namespaceRelations, setNamespaceRelations] = useState<NamespaceRelations>({});

  useEffect(() => {
    hub
      .relationsNamespaces()
      .then((res) =>
        setNamespaceRelations(
          Object.fromEntries(Object.entries(res.namespaces).map(([ns, v]) => [ns, v.directRelations])),
        ),
      )
      .catch(() => {
        // Left empty on purpose — the free-text fallback is the safe default.
      });
  }, []);

  return namespaceRelations;
}
